import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { ReservationModel, ReservationPlaceModel } from 'shared/models';
import { DateType, UiControlPropsType } from 'shared/types';
import { toDate } from 'shared/utilities';
import { useObservable } from 'shared/hooks';

import { UiReservationPickerReservation } from './components/UiReservationPickerReservation';

import './styles.scss';

type IntervalGroupType = { id: string; startAtDate: DateType; endAtDate: DateType; intervals: DateType[] };

type PropsType = UiControlPropsType<ReservationModel> & {
    date: string;
    reservationPlace: ReservationPlaceModel;
};

export const UiReservationPicker = observer(function UiReservationPicker({
    reservationPlace,
    value,
    date,
    onChange = () => undefined,
    name = 'reservation'
}: PropsType) {
    const store = useObservable({
        intervalGroupId: null as string | null,
        startAtDate: null as DateType | null,
        endAtDate: null as DateType | null,
        hoverAtDate: null as DateType | null,

        intervals: [] as string[],
        reserved: [] as string[],
        intervalGroups: [] as IntervalGroupType[],
    });

    useEffect(() => {
        const reserved: string[] = [];
        reservationPlace.reservations
            .filter(reservation => reservation.id !== value?.id)
            .forEach(reservation => {
                let date = reservation.startAtDate.clone();
                while (date.isBefore(reservation.endAtDate)) {
                    reserved.push(date.format('HH:mm'));
                    date = date.add(30, 'minutes');
                }
            });

        const intervalGroups: IntervalGroupType[] = [];
        let time = toDate(date).set('hour', 8).set('minute', 0);
        const endOfDay = time.set('hour', 23).set('minute', 59);
        let intervalGroup: IntervalGroupType | null = null;
        while (time.isBefore(endOfDay)) {
            const timeId = time.format('HH:mm');
            if (intervalGroup === null && !reserved.includes(timeId)) {
                intervalGroup = {
                    id: timeId,
                    startAtDate: time.clone(),
                    endAtDate: time.clone().add(30, 'minutes'),
                    intervals: [],
                };
            }

            if (intervalGroup && !reserved.includes(timeId)) {
                intervalGroup.intervals.push(time.clone());
                intervalGroup.endAtDate = intervalGroup.endAtDate.add(30, 'minutes');
            }

            if (intervalGroup && reserved.includes(timeId)) {
                intervalGroups.push(intervalGroup);
                intervalGroup = null;
            }

            time = time.add(30, 'minutes');
        }
        if (intervalGroup) {
            intervalGroups.push(intervalGroup);
        }

        store.update({
            reserved,
            intervalGroups,
        });
    }, [store, reservationPlace, date, value?.id]);

    return (
        <div className='ui-new-reservation-picker'>
            {reservationPlace.previewImageUrl && <div className='ui-new-reservation-picker__image' style={{ backgroundImage: `url(${reservationPlace.previewImageUrl})` }}></div>}
            {!reservationPlace.previewImageUrl && <div className='ui-new-reservation-picker__image' />}
            <div className='ui-new-reservation-picker__header'>
                <div className='ui-new-reservation-picker__name'>{reservationPlace.name}</div>
                <div className='ui-new-reservation-picker__description'>{reservationPlace.description}</div>
            </div>
            <div className='ui-new-reservation-picker__inner'>
                {store.intervalGroups.map(intervalGroup => (
                    <div key={intervalGroup.id} className='ui-new-reservation-picker-interval-group' style={{ order: intervalGroup.startAtDate.unix() }}>
                        {intervalGroup.intervals.map(interval => (
                            <div
                                key={interval.format()}
                                className={classnames('ui-new-reservation-picker-interval', {
                                    'ui-new-reservation-picker-interval--selected':
                                        store.startAtDate &&
                                        store.endAtDate &&
                                        (interval.isSame(store.startAtDate) || (interval.isAfter(store.startAtDate) && interval.isBefore(store.endAtDate))),
                                    'ui-new-reservation-picker-interval--selecting':
                                        store.startAtDate &&
                                        !store.endAtDate &&
                                        (interval.isSame(store.startAtDate) ||
                                            (interval.isAfter(store.startAtDate) &&
                                                intervalGroup.id === store.intervalGroupId &&
                                                (interval.isBefore(store.hoverAtDate) || interval.isSame(store.hoverAtDate)))),
                                })}
                                onMouseEnter={() => {
                                    store.set('hoverAtDate', interval.clone());
                                }}
                                onClick={() => {
                                    if (store.startAtDate && interval.isSame(store.startAtDate)) {
                                        store.set('endAtDate', store.startAtDate.add(30, 'minutes'));
                                        onChange({
                                            name,
                                            value: new ReservationModel({
                                                ...(value || {}),
                                                startAt: store.startAtDate.format(),
                                                endAt: interval.add(30, 'minutes').format(),
                                                reservationPlaceId: reservationPlace.id,
                                                reservationPlace,
                                            }),
                                        });
                                        return;
                                    }
                                    if (interval.isBefore(store.startAtDate) || intervalGroup.id !== store.intervalGroupId || store.endAtDate) {
                                        store.update({
                                            startAtDate: interval.clone(),
                                            endAtDate: null,
                                            intervalGroupId: intervalGroup.id,
                                        });
                                        return;
                                    }
                                    if (!store.startAtDate) {
                                        store.update({
                                            startAtDate: interval.clone(),
                                            intervalGroupId: intervalGroup.id,
                                        });
                                        return;
                                    }
                                    if (interval.isBefore(store.startAtDate)) {
                                        store.update({
                                            startAtDate: interval.clone(),
                                            endAtDate: null,
                                            intervalGroupId: intervalGroup.id,
                                        });
                                        return;
                                    }
                                    if (interval.isAfter(store.startAtDate) && intervalGroup.id === store.intervalGroupId) {
                                        store.set('endAtDate', interval.add(30, 'minutes'));
                                        onChange({
                                            name,
                                            value: new ReservationModel({
                                                ...(value || {}),
                                                startAt: store.startAtDate.format(),
                                                endAt: interval.add(30, 'minutes').format(),
                                                reservationPlaceId: reservationPlace.id,
                                                reservationPlace,
                                            }),
                                        });
                                        return;
                                    }
                                }}
                            >
                                {interval.format('HH:mm')}
                            </div>
                        ))}
                    </div>
                ))}
                {reservationPlace.reservations
                    .filter(reservation => reservation.id !== value?.id)
                    .map(reservation => (
                        <UiReservationPickerReservation
                            key={reservation.id}
                            reservation={reservation}
                            style={{
                                order: reservation.startAtDate.unix(),
                                height: (reservation.endAtDate.diff(reservation.startAtDate, 'minutes')) * 32 / 30
                            }}
                        />
                    ))}
            </div>
        </div>
    );
});
